import { type CSSProperties, useEffect, useRef } from 'react';

/** A text selection, with its screen bounds. */
export type SelectedText = {
  bounds: { bottom: number; left: number; top: number };
  text: string;
};

/** How long the handles of a touch selection rest before its toolbar shows, in milliseconds. */
export const TOUCH_SELECTION_SETTLE_MS = 400;

/** The page's text selection and its range, if it is one non-empty range within `container`. */
export function selectionWithin(
  container: Element | null,
): { range: Range; selection: SelectedText } | null {
  const selected = window.getSelection();
  if (selected === null || selected.isCollapsed || selected.rangeCount !== 1) return null;
  const range = selected.getRangeAt(0);
  if (container !== null && !container.contains(range.commonAncestorContainer)) return null;
  const text = selected.toString().trim();
  if (text.length === 0) return null;
  const bounds = range.getBoundingClientRect();
  return {
    range,
    selection: { bounds: { bottom: bounds.bottom, left: bounds.left, top: bounds.top }, text },
  };
}

function coarsePointer(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
}

/**
 * A selection made with a finger: no mouseup comes, its handles move it. Once they rest, the
 * selection (and the element its range starts from) is handed over. Mice keep their mouseup.
 */
export function useTouchSelection(
  onSelect: (selection: SelectedText, range: Range) => void,
  enabled = true,
): void {
  const current = useRef(onSelect);
  current.current = onSelect;
  useEffect(() => {
    if (!enabled || !coarsePointer()) return;
    let timer = 0;
    const onChange = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const found = selectionWithin(null);
        if (found !== null) current.current(found.selection, found.range);
      }, TOUCH_SELECTION_SETTLE_MS);
    };
    document.addEventListener('selectionchange', onChange);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('selectionchange', onChange);
    };
  }, [enabled]);
}

/** The element a range starts from (its text node's parent). */
export function rangeElement(range: Range): Element | null {
  const node = range.commonAncestorContainer;
  return node instanceof Element ? node : node.parentElement;
}

/** Below this width the toolbar spans the screen, its actions sharing the row. */
const NARROW_SCREEN = 480;

/**
 * Where a selection's toolbar stands: above the selection with a mouse; under it on a touch
 * screen, where the system's own menu sits above; across a phone's width, within the screen.
 */
export function selectionToolbarStyle(
  bounds: SelectedText['bounds'],
  width: number,
): CSSProperties {
  const below = coarsePointer();
  const top = below
    ? Math.min(bounds.bottom + 12, window.innerHeight - 64)
    : Math.max(12, bounds.top - 52);
  if (window.innerWidth < NARROW_SCREEN) return { left: 12, right: 12, top: Math.max(12, top) };
  return { left: Math.max(12, Math.min(bounds.left, window.innerWidth - width)), top };
}
